// Company-Policy acknowledgement gate.
//
// Two-layer UX:
//   • Banner across the top of the app whenever the user has one or more
//     unacknowledged company_policy documents. Non-dismissible; clicking
//     "Read & Accept" opens the modal.
//   • Blocking modal that shows the current policy (title + description +
//     attachments) with a mandatory pledge checkbox. User cannot bypass it
//     — until they click "I Read & I Accept" (or all policies are cleared)
//     the rest of the portal is greyed out and non-interactive.
//
// Polls /api/documents/policy/pending every 60s so newly-published policies
// surface without a page reload.

import { useEffect, useState, useCallback } from "react";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, ExternalLink, Download } from "lucide-react";
import { fmtDateTimeIST } from "../../lib/datetime";
import { attachmentFlavor, fmtSize } from "./docConstants";

export default function PolicyAckGate() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [pledge, setPledge] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [pRes, tRes] = await Promise.all([
        api.get("/documents/policy/pending"),
        api.get("/documents/policy/pledge-text"),
      ]);
      setPending(pRes.data || []);
      setPledge(tRes.data?.pledge || "");
    } catch { /* silent */ }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Auto-open modal once we have pending items — user cannot dismiss.
  useEffect(() => {
    if (pending.length > 0 && !showModal) setShowModal(true);
    if (pending.length === 0 && showModal) setShowModal(false);
  }, [pending.length, showModal]);

  useEffect(() => { setAccepted(false); }, [pending[0]?.id]);

  const current = pending[0];
  const downloadAtt = async (docId, idx, filename) => {
    try {
      const r = await api.get(`/documents/${docId}/attachment/${idx}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "policy"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const accept = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/documents/${current.id}/acknowledge`, {});
      toast.success(`Policy accepted: ${current.title}`);
      setPending((p) => p.filter((x) => x.id !== current.id));
      setAccepted(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (!user?.id || pending.length === 0) return null;

  return (
    <>
      {/* Persistent red banner at the very top of every route */}
      <div
        className="fixed top-0 inset-x-0 z-[80] bg-gradient-to-r from-rose-700 to-red-700 text-white shadow-md"
        role="alert"
        data-testid="policy-ack-banner"
      >
        <div className="mx-auto max-w-[1400px] px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
          <div className="text-[12.5px] font-semibold min-w-0 flex-1 truncate">
            You must read &amp; accept {pending.length} pending company polic{pending.length > 1 ? "ies" : "y"} before continuing.
          </div>
          <Button
            onClick={() => setShowModal(true)}
            size="sm"
            className="bg-white text-rose-700 hover:bg-white/90 h-8 font-bold"
            data-testid="policy-ack-open-btn"
          >
            Read &amp; Accept
          </Button>
        </div>
      </div>
      {/* Reserve vertical space for the banner so it doesn't overlap header */}
      <div className="h-11" aria-hidden="true" />

      {/* Blocking modal — cannot be dismissed via ESC or overlay click. */}
      <Dialog
        open={showModal}
        onOpenChange={() => { /* intentionally noop — cannot close */ }}
      >
        <DialogContent
          className="max-w-2xl max-h-[92vh] overflow-y-auto"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="policy-ack-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <ShieldAlert className="w-5 h-5" /> Mandatory: Company Policy
            </DialogTitle>
            <DialogDescription>
              {current?.title}
              {pending.length > 1 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 px-2 py-0.5 text-[10.5px] font-bold">
                  1 of {pending.length}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {current?.description && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 px-3 py-2 text-[13px] whitespace-pre-wrap leading-relaxed text-slate-800 dark:text-slate-200 max-h-56 overflow-y-auto">
              {current.description}
            </div>
          )}

          {(current?.attachments || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1.5">
                Attachments — please review
              </div>
              <div className="space-y-1.5">
                {current.attachments.map((a, i) => {
                  const { Icon, cls } = attachmentFlavor(a);
                  return (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${cls}`}><Icon size={14} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200 truncate">
                          {a.type === "url" ? (a.hostname || a.external_url) : a.filename}
                        </div>
                        <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">
                          {a.type === "url" ? a.external_url : `${fmtSize(a.size)}`}
                        </div>
                      </div>
                      {a.type === "url" ? (
                        <a href={a.external_url} target="_blank" rel="noopener noreferrer"><Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white"><ExternalLink size={11} className="mr-1" /> Open</Button></a>
                      ) : (
                        <Button size="sm" onClick={() => downloadAtt(current.id, i, a.filename)} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"><Download size={11} className="mr-1" /> Download</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-950/30 px-3 py-2.5 text-[12.5px] leading-relaxed text-rose-900 dark:text-rose-200">
            {pledge || "Loading pledge…"}
          </div>

          <label className="flex items-start gap-2 text-[12.5px] text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5"
              data-testid="policy-ack-checkbox"
            />
            <span>I have read the policy and agree to be bound by the terms &amp; conditions stated above.</span>
          </label>

          {current?.published_at && (
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400 font-mono">
              Published on {fmtDateTimeIST(current.published_at)}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              onClick={accept}
              disabled={!accepted || busy}
              className="bg-rose-600 hover:bg-rose-700 text-white w-full"
              data-testid="policy-ack-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Recording…" : "I Read & I Accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
